<template>
  <div :class="tableClasses">
    <slot name="header-title" :title="title">
      <GenericTitle v-if="title" :text="title" class="mb-[30px]" />
    </slot>
    <div
      :class="[
        { 'overflow-x-auto custom-blue-scrollbar': data.length !== 0 },
        containerClasses,
      ]"
    >
      <slot name="before_table" />
      <table class="w-full mb-[10px]">
        <thead>
          <tr>
            <template v-for="(header, index) in headers" :key="index">
              <th
                :class="[header.th_classes]"
                class="px-[20px] py-[9px] text-left text-grey-mid text-[10px] leading-[14px] tracking-[-0.1px] font-normal uppercase"
              >
                <span
                  class="inline-flex items-center gap-x-[5px] th-sortable-icons"
                >
                  {{ header.title }}

                  <span
                    v-if="header.sortable"
                    class="flex flex-col gap-y-[3px]"
                  >
                    <svg
                      class="cursor-pointer"
                      :class="{
                        active:
                          sorting.sort_by == header.key &&
                          sorting.sort_type == 'asc',
                      }"
                      xmlns="http://www.w3.org/2000/svg"
                      width="10"
                      height="5"
                      viewBox="0 0 10 5"
                      fill="none"
                      @click="sortData(header, 'asc')"
                    >
                      <path
                        d="M4.75 0L7.9976 4.5H1.5024L4.75 0Z"
                        fill="#7F7F7F"
                      />
                    </svg>

                    <svg
                      class="cursor-pointer"
                      :class="{
                        active:
                          sorting.sort_by == header.key &&
                          sorting.sort_type == 'desc',
                      }"
                      xmlns="http://www.w3.org/2000/svg"
                      width="10"
                      height="5"
                      viewBox="0 0 10 5"
                      fill="none"
                      @click="sortData(header, 'desc')"
                    >
                      <path
                        d="M4.75 5L1.5024 0.5L7.9976 0.5L4.75 5Z"
                        fill="#7F7F7F"
                      />
                    </svg>
                  </span>
                </span>
              </th>
            </template>
          </tr>
        </thead>
        <tbody class="relative">
          <tr v-if="loading" class="absolute left-0 top-0 h-1.5 w-full">
            <td class="absolute w-full" :colspan="headers.length">
              <GenericProgressBarLoader />
            </td>
          </tr>

          <template v-if="data.length !== 0">
            <template v-for="(item, index) in data">
              <slot name="row" :item="item" :index="index" :headers="headers">
                <tr
                  :key="index"
                  class="border-t border-grey-separator"
                  :class="{ 'border-b': index + 1 == data.length }"
                >
                  <template
                    v-for="(header, hindex) in headers"
                    :key="`${index}_${hindex}`"
                  >
                    <td
                      :class="header.td_classes"
                      class="p-[22px_20px] text-left leading-[20px] text-[14px] font-medium text-charcoal tracking-[0.25px]"
                    >
                      <slot :name="header.key" :item="item" :index="index">
                        {{
                          typeof header.formatter == 'function'
                            ? header.formatter(item)
                            : getValInObj(item, header.key, header.falsy)
                        }}
                      </slot>
                    </td>
                  </template>
                </tr>
              </slot>
            </template>
          </template>

          <template v-else>
            <tr>
              <td
                class="border-t border-grey-separator text-center pb-0"
                :colspan="headers.length"
              >
                <div
                  class="mb-0 leading-[20px] text-[14px] text-grey-mid h-[100px] font-medium flex items-center justify-center"
                >
                  {{ loading ? $t('loading') + '...' : $t('no_data') }}
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div :class="footerClasses">
      <slot name="before-pagination"></slot>
      <div
        v-if="lastPage > 1"
        class="flex items-center justify-center mt-[40px]"
      >
        <GenericPagination
          v-model:current-page="page"
          :last-page="lastPage"
          light
          @change="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import GenericProgressBarLoader from '@/components/generic/ProgressBarLoader.vue';
import { useHelpers } from '@/composables/useHelpers';
import { computed, ref, watch } from 'vue';
const emit = defineEmits(['filtered']);

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  tableClasses: {
    type: String,
    default: 'rounded-[6px] p-[38px_30px_29px_30px] shadow-default bg-white',
  },
  containerClasses: {
    type: String,
    default: '',
  },
  footerClasses: {
    type: String,
    default: '',
  },
  headers: {
    type: Array,
    default: () => [],
  },
  data: {
    type: Array,
    default: () => [],
  },
  pagination: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const { getValInObj } = useHelpers();

const paginationData = ref(props.pagination);
const sorting = ref({
  sort_by: '',
  sort_type: '',
});

const page = computed(() => paginationData.value?.current_page);
const lastPage = computed(() => paginationData.value?.last_page);
watch(
  () => props.pagination,
  (val) => (paginationData.value = val)
);

/* Sorts data if we dont have seperate sortings like up and down arrows */
// const sortData = (header) => {
//   if (!header.sortable) return;
//   const { sort_type, sort_by } = sorting.value;
//   sorting.value.sort_by = header.key;
//   sorting.value.sort_type = sort_type == '' || header.key != sort_by ? 'asc' : sort_type == 'asc' ? 'desc' : 'asc';
//   emit('filtered', {
//     page: page.value,
//     orderBy: sorting.value.sort_by + '+' + sorting.value.sort_type,
//   });
// };

const sortData = (header, type) => {
  if (sorting.value.sort_by == header.key && sorting.value.sort_type == type)
    return;
  sorting.value.sort_by = header.key;
  sorting.value.sort_type = type;
  emit('filtered', {
    page: page.value,
    orderBy: sorting.value.sort_by + '+' + sorting.value.sort_type,
  });
};

const handlePageChange = (page) => {
  const params = { page };
  if (sorting.value.sort_by)
    params.orderBy = sorting.value.sort_by + '+' + sorting.value.sort_type;
  paginationData.value.current_page = page;
  emit('filtered', params);
};
</script>

<style scoped></style>
